<script setup>
import { ref, computed, defineEmits, defineProps } from "vue"
import { useI18n } from "vue-i18n"
import { notify } from "notiwind"
import { useStore } from "vuex"
import { EyeIcon, EyeSlashIcon } from "@heroicons/vue/24/outline"
import { XCircleIcon } from "@heroicons/vue/20/solid"
import { Form as VeeForm, Field, ErrorMessage } from "vee-validate"
import * as yup from "yup"
import PasswordStrength from "@common/components/password-strength/PasswordStrength.vue"
import ExistingInstallerSiret from "@/partials/signup/ExistingInstallerSiret.vue"

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})
const emit = defineEmits(["update:modelValue", "success"])
const apiErrors = ref({
  duplicateEmail: false,
  duplicateSiret: false,
})
const { t } = useI18n()
const loading = ref(false)
const store = useStore()
const showConfirmPassword = ref(false)
const showPassword = ref(false)

const form = computed({
  get() {
    return props.modelValue
  },

  set(value) {
    return emit("update:modelValue", value)
  },
})

const schema = yup.object({
  companyName: yup.string().required().label(t("sign-up.company-name")),
  email: yup.string().email().required().label(t("sign-up.email.label")),
  phone: yup
    .string()
    .matches(/^[0-9]+$/)
    .required()
    .min(10)
    .label(t("sign-up.phone-number.label")),
  siret: yup
    .string()
    .matches(/^[0-9]+$/)
    .required()
    .min(14, "Siret est composé de 14 chiffres")
    .max(14, "Siret est composé de 14 chiffres")
    .label(t("sign-up.siret.placeholder")),
  password: yup
    .string()
    .required(t("sign-up.password.required"))
    .min(8, t("sign-up.password.min-length"))
    .matches(/[A-Z]/, t("sign-up.password.upper-case"))
    .matches(/[a-z]/, t("sign-up.password.lower-case"))
    .matches(/[@\$!%*?&]/, t("sign-up.password.symbol")) // Escaped the "$" here
    .matches(/\d/, t("sign-up.password.number"))
    .label(t("sign-up.password.label")),
  confirmPassword: yup
    .string()
    .required()
    .oneOf(
      [yup.ref("password"), null],
      t("sign-up.confirm-password.should-match"),
    )
    .label(t("sign-up.confirm-password.label")),
})

async function submit() {
  try {
    loading.value = true
    resetApiErrors()
    await axios.post("/auth/installer/register", {
      companyName: form.value.companyName,
      foundedDate: form.value.foundedDate,
      email: form.value.email,
      siret: form.value.siret,
      phone: form.value.phone,
      password: form.value.password,
    })

    notify(
      {
        title: t("snackbars.success.title"),
        text: t("snackbars.register-success"),
        type: "info",
        group: "foo",
      },
      4000,
    )
    emit("success")
  } catch (error) {
    if (error.response.data.status === 409) {
      if (error.response.data.code == "duplicate.siret") {
        apiErrors.value.duplicateSiret = true
      } else {
        apiErrors.value.duplicateEmail = true
        notify(
          {
            title: t("snackbars.errors.title"),
            text: t("snackbars.errors.register-failed"),
            type: "warning",
            group: "foo",
          },
          4000,
        )
      }
    }
  } finally {
    await store.dispatch("auth/logout")
    loading.value = false
  }
}

function resetApiErrors() {
  apiErrors.value = {
    duplicateSiret: false,
    duplicateEmail: false,
  }
}
</script>

<template>
  <VeeForm
    v-slot="{ meta, errors, handleSubmit }"
    :validation-schema="schema"
    as="div"
  >
    <form @submit="handleSubmit($event, submit)">
      <div class="flex flex-wrap -mx-3 mb-4">
        <div class="w-full px-3">
          <label
            class="block text-gray-800 text-sm font-medium mb-1"
            for="companyName"
            >{{ $t("sign-up.company-name") }}
            <span class="text-red-600">*</span></label
          >
          <Field
            id="companyName"
            v-model="form.companyName"
            type="text"
            name="companyName"
            class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
            :placeholder="$t('sign-up.enter-your-company-name')"
            :class="[
                'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6',
                errors.companyName
                ? ' text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500'
                : '',
            ]"
            required
          />
          <ErrorMessage name="companyName" class="mt-2 text-sm text-red-600" />
        </div>
      </div>
      <div class="flex flex-wrap -mx-3 mb-4">
        <div class="w-full px-3">
          <label
            class="block text-gray-800 text-sm font-medium mb-1"
            for="siret"
            >{{ $t("common.siret") }} <span class="text-red-600">*</span></label
          >
          <Field
            id="siret"
            v-model="form.siret"
            name="siret"
            type="number"
            class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
            :placeholder="$t('common.enter-your-siret')"
            :class="[
              'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6',
              errors.siret
                ? ' text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500'
                : '',
            ]"
            required
          />
          <ErrorMessage name="siret" class="mt-2 text-sm text-red-600" />
        </div>
      </div>
      <div class="flex flex-wrap -mx-3 mb-4">
        <div class="w-full px-3">
          <label
            class="block text-gray-800 text-sm font-medium mb-1"
            for="email"
            >{{ $t("common.email") }} <span class="text-red-600">*</span></label
          >
          <Field
            id="email"
            v-model.trim="form.email"
            name="email"
            type="email"
            class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
            :placeholder="$t('common.enter-your-email')"
            :class="[
              'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6',
              errors.email
                ? ' text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500'
                : '',
            ]"
            required
          />
          <ErrorMessage name="email" class="mt-2 text-sm text-red-600" />
        </div>
      </div>
      <div class="flex flex-wrap -mx-3 mb-4">
        <div class="w-full px-3">
          <label
            class="block text-gray-800 text-sm font-medium mb-1"
            for="phone"
            >{{ $t("common.phone-number") }}
            <span class="text-red-600">*</span></label
          >
          <div class="relative">
            <div class="absolute inset-y-0 left-0 flex items-center">
              <label for="country" class="sr-only">Country</label>
              <select
                id="country"
                name="country"
                autocomplete="country"
                class="h-full rounded-md border-transparent bg-transparent py-0 text-gray-500 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option>+33</option>
              </select>
            </div>
            <Field
              id="phone"
              v-model="form.phone"
              type="text"
              name="phone"
              class="block pl-20 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
              :placeholder="$t('common.enter-your-phone-number')"
              :class="[
                'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6',
                errors.phone
                  ? ' text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500'
                  : '',
              ]"
              required
            />
          </div>
          <ErrorMessage name="phone" class="mt-2 text-sm text-red-600" />
        </div>
      </div>
      <div class="flex flex-wrap -mx-3 mb-4">
        <div class="w-full px-3">
          <label
            class="block text-gray-800 text-sm font-medium mb-1"
            for="password"
            >{{ $t("common.password") }}
            <span class="text-red-600">*</span></label
          >
          <div class="relative">
            <Field
              id="password"
              v-model="form.password"
              :type="!showPassword ? 'password' : 'text'"
              name="password"

              :class="[
                'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6',
                errors.password
                  ? ' text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500'
                  : '',
              ]"
              :placeholder="$t('common.enter-your-password')"
              required
            />
            <button
              type="button"
              class="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-500"
              aria-label="Toggle password visibility"
              @click="showPassword = !showPassword"
            >
              <template v-if="showPassword">
                <EyeSlashIcon class="w-5 h-5" />
              </template>
              <template v-else>
                <EyeIcon class="w-5 h-5" />
              </template>
            </button>
          </div>
          <ErrorMessage name="password" class="mt-2 text-sm text-red-600" />
          <PasswordStrength :password="form.password" class="mt-2" />
        </div>
      </div>
      <div class="flex flex-wrap -mx-3 mb-4">
        <div class="w-full px-3">
          <label
            class="block text-gray-800 text-sm font-medium mb-1"
            for="confirmPassword"
            >{{ $t("sign-up.password-confirm") }}
            <span class="text-red-600">*</span></label
          >
          <div class="relative">
            <Field
              id="confirmPassword"
              v-model="form.confirmPassword"
              :type="!showConfirmPassword ? 'password' : 'text'"
              name="confirmPassword"
              :class="[
                'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6',
                errors.confirmPassword
                  ? ' text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500'
                  : '',
              ]"
              :placeholder="$t('common.enter-your-password')"
              required
            />
            <button
              type="button"
              class="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-500"
              aria-label="Toggle password visibility"
              @click="showConfirmPassword = !showConfirmPassword"
            >
              <template v-if="showConfirmPassword">
                <EyeSlashIcon class="w-5 h-5" />
              </template>
              <template v-else>
                <EyeIcon class="w-5 h-5" />
              </template>
            </button>
          </div>
          <ErrorMessage
            name="confirmPassword"
            class="mt-2 text-sm text-red-600"
          />
        </div>

        <div
          v-if="apiErrors.duplicateEmail"
          class="rounded-md bg-red-50 p-4 m-2"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <XCircleIcon class="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">
                {{ $t("sign-up.errors.duplicateEmailEnabled") }}
              </h3>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-wrap -mx-3 mt-6">
        <div class="w-full px-3">
          <button
            :disabled="!meta.valid || loading"
            class="btn text-white bg-black hover:bg-grey w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <template v-if="loading">
              <svg
                class="mr-3 h-5 w-5 animate-spin text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {{ $t("common.processing") }}...
            </template>
            <span v-else> {{ $t("common.buttons.confirm") }}</span>
          </button>
        </div>
      </div>
      <div class="text-sm text-gray-500 text-center mt-3">
        <span v-safe-html="$t('sign-up.tc')"></span>
      </div>
    </form>
    <ExistingInstallerSiret
      v-model="apiErrors.duplicateSiret"
      :siret="form.siret"
    />
  </VeeForm>
</template>
